import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AUTH_ONBOARDING_RESUME_KEY } from '@/constants/onboardingResume';
import {
  CUSTOMER_ACTIVE_CONTEXT_KEY,
  CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY,
  CUSTOMER_LOGIN_TYPE_MISMATCH_KEY,
} from '@/constants/customerAuthRouting';

// Re-export for convenience

interface ClientProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone_number: string | null;
  client_type: string;
  status: string;
  country_of_residence: string | null;
  nationality: string | null;
  date_of_birth: string | null;
  company_name: string | null;
  risk_level: string | null;
  hashtag: string | null;
  system_id: string | null;
  onboarding_session_id?: string | null;
}

interface PendingProfileLookup {
  id: string;
  status: string;
  client_type: string;
  email: string | null;
  onboarding_session_id: string | null;
}

interface ProfileContext {
  client_id: string;
  client_type: string;
  label: string;
  role?: string;
}

interface CustomerAuthContextType {
  user: User | null;
  session: Session | null;
  profile: ClientProfile | null;
  profileLoaded: boolean;
  loading: boolean;
  /** Set when user has a profile but it is not yet approved (e.g. status=pending) */
  profilePending: boolean;
  availableContexts: ProfileContext[];
  switchProfile: (clientId: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  register: (email: string, password: string) => Promise<{ error: string | null }>;
  /** Set true before MFA verify to suppress SIGNED_OUT from token rotation */
  setAuthGuard: (active: boolean) => void;
  /** Call after MFA verify to force profile load without waiting for bootstrap */
  loadProfileAfterMfa: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

const PROFILE_SELECT = 'id, full_name, email, phone_number, client_type, status, country_of_residence, nationality, date_of_birth, company_name, risk_level, hashtag, system_id, onboarding_session_id';
const PENDING_STATUSES = ['pending', 'pending_review', 'review', 'submitted'];
const AUTH_SESSION_READY_ATTEMPTS = 10;
const AUTH_SESSION_READY_DELAY_MS = 300;
const RESUMABLE_SESSION_STATUSES = new Set(['in_progress', 'draft']);
const TERMINAL_ONBOARDING_STEPS = new Set(['success', 'corporate-success']);
const ONBOARDING_RESUME_SENSITIVE_KEYS = new Set([
  'password_hash',
  'passcode',
  'hashed_passcode',
  'session_token',
  'session_expiry',
  'otp_code',
  'otp_expiry',
  'email_otp_code',
  'email_otp_expiry',
  'sms_otp_code',
  'sms_otp_expiry',
  'resume_token',
]);

function sanitizeOnboardingResumeData(onboardingData: Record<string, unknown> | null | undefined) {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(onboardingData || {})) {
    if (!ONBOARDING_RESUME_SENSITIVE_KEYS.has(key)) {
      result[key] = value;
    }
  }

  return {
    ...result,
    resumeAuthenticated: true,
  };
}

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profilePending, setProfilePending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [availableContexts, setAvailableContexts] = useState<ProfileContext[]>([]);
  const bootstrappedRef = useRef(false);

  // Guard: when signIn / MFA is in progress, ignore SIGNED_OUT events from
  // Supabase's internal session cleanup (token rotation).
  const signingInRef = useRef(false);
  // Debounce SIGNED_OUT to handle token rotation
  const signOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistResumableOnboarding = useCallback(async (pendingProfiles: PendingProfileLookup[]) => {
    for (const pendingProfile of pendingProfiles) {
      const isResumableSession = (data: {
        email: string | null;
        current_step: string;
        account_type: string;
        status: string;
        onboarding_data: Record<string, unknown> | null;
      } | null | undefined) => Boolean(
        data &&
        RESUMABLE_SESSION_STATUSES.has(data.status) &&
        !TERMINAL_ONBOARDING_STEPS.has(data.current_step)
      );

      let sessionData:
        | {
            email: string | null;
            current_step: string;
            account_type: string;
            status: string;
            onboarding_data: Record<string, unknown> | null;
          }
        | null
        = null;

      if (pendingProfile.onboarding_session_id) {
        const { data } = await supabase
          .from('onboarding_sessions')
          .select('email, current_step, account_type, status, onboarding_data')
          .eq('id', pendingProfile.onboarding_session_id)
          .maybeSingle();

        sessionData = data as typeof sessionData;
      }

      if ((!isResumableSession(sessionData)) && pendingProfile.email) {
        const { data } = await supabase
          .from('onboarding_sessions')
          .select('email, current_step, account_type, status, onboarding_data')
          .eq('email', pendingProfile.email.toLowerCase())
          .eq('account_type', pendingProfile.client_type)
          .order('updated_at', { ascending: false })
          .limit(5);

        sessionData = ((data || []).find(candidate =>
          RESUMABLE_SESSION_STATUSES.has(candidate.status) &&
          !TERMINAL_ONBOARDING_STEPS.has(candidate.current_step)
        ) || null) as typeof sessionData;
      }

      if (!isResumableSession(sessionData)) continue;

      const onboardingData = (sessionData.onboarding_data || {}) as Record<string, unknown>;
      const sessionToken = typeof onboardingData.session_token === 'string'
        ? onboardingData.session_token
        : null;

      sessionStorage.setItem(AUTH_ONBOARDING_RESUME_KEY, JSON.stringify({
        currentStep: sessionData.current_step,
        accountType: sessionData.account_type || pendingProfile.client_type,
        onboardingData: sanitizeOnboardingResumeData(onboardingData),
        sessionToken,
        email: (sessionData.email || pendingProfile.email || '').toLowerCase(),
      }));

      return true;
    }

    return false;
  }, []);

  const waitForSessionReady = useCallback(async (expectedUserId: string) => {
    for (let attempt = 0; attempt < AUTH_SESSION_READY_ATTEMPTS; attempt++) {
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData?.session?.user ?? null;

      if (sessionUser?.id === expectedUserId && sessionData.session?.access_token) {
        return sessionData.session;
      }

      await new Promise(resolve => setTimeout(resolve, AUTH_SESSION_READY_DELAY_MS));
    }

    return null;
  }, []);

  const fetchProfile = useCallback(async (userId: string, isRefresh = false) => {
    if (!isRefresh) setProfileLoaded(false);
    setProfilePending(false);
    try {
      const readySession = await waitForSessionReady(userId);
      if (!readySession?.user) {
        setProfile(null);
        setAvailableContexts([]);
        return;
      }

      // Auto-accept any pending corporate invitations for this user's email
      try {
        await supabase.functions.invoke('corporate-members', {
          body: { action: 'auto_accept_invitations' },
        });
      } catch (autoErr) {
        console.warn('Auto-accept invitations check failed (non-blocking):', autoErr);
      }

      // Fetch ALL approved/active profiles for this user (owned profiles)
      const { data: ownedProfiles, error } = await supabase
        .from('client_profiles')
        .select(PROFILE_SELECT)
        .eq('user_id', userId)
        .eq('status', 'approved');

      // Also fetch corporate profiles where user is an active member (invited users)
      const { data: memberRows } = await supabase
        .from('corporate_members')
        .select('client_id, role')
        .eq('user_id', userId)
        .eq('is_active', true)
        .eq('kyc_status', 'approved');

      const memberClientIds = (memberRows || [])
        .map(m => m.client_id)
        .filter(id => !(ownedProfiles || []).some(p => p.id === id));

      let memberProfiles: typeof ownedProfiles = [];
      if (memberClientIds.length > 0) {
        const { data: mp } = await supabase
          .from('client_profiles')
          .select(PROFILE_SELECT)
          .in('id', memberClientIds)
          .eq('status', 'approved');
        memberProfiles = mp || [];
      }

      // Merge owned + member profiles (deduplicated)
      const profiles = [...(ownedProfiles || []), ...(memberProfiles || [])];

      if ((error && memberProfiles.length === 0) || profiles.length === 0) {
        // Check if user has any profiles — approved takes priority, pending blocks
        const { data: allProfiles } = await supabase
          .from('client_profiles')
          .select('id, status, client_type, email, onboarding_session_id')
          .eq('user_id', userId);

        // If ANY of the user's profiles is approved, we already tried fetching them
        // above and failed — means the approved profile's user_id isn't linked yet.
        // Attempt auto-link via edge function rather than showing "under review".
        const hasApproved = allProfiles?.some(p => p.status === 'approved');
        if (hasApproved) {
          // Fall through to auto-create/link below
        } else {
          const pendingProfiles = (allProfiles?.filter(p => PENDING_STATUSES.includes(p.status)) || []) as PendingProfileLookup[];
          const hasResumableOnboarding = pendingProfiles.length > 0
            ? await persistResumableOnboarding(pendingProfiles)
            : false;

          if (hasResumableOnboarding) {
            setProfile(null);
            setProfilePending(false);
            setAvailableContexts([]);
            return;
          }

          const pendingProfile = pendingProfiles[0];
          if (pendingProfile) {
            // User has a profile but it's genuinely not approved yet
            setProfile(null);
            setProfilePending(true);
            setAvailableContexts([]);
            return;
          }
        }

        // No profile at all — try auto-create via edge function
        try {
          const { data: { session: s } } = await supabase.auth.getSession();
          if (s?.access_token) {
            const res = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-api/profile/ensure`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${s.access_token}`,
                  'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                },
              }
            );
            if (res.ok) {
              const { data: newProfiles } = await supabase
                .from('client_profiles')
                .select(PROFILE_SELECT)
                .eq('user_id', userId)
                .eq('status', 'approved');
              if (newProfiles && newProfiles.length > 0) {
                await resolveActiveProfile(newProfiles as ClientProfile[]);
                return;
              }
            }
          }
          setProfile(null);
          setAvailableContexts([]);
        } catch {
          setProfile(null);
          setAvailableContexts([]);
        }
      } else {
        await resolveActiveProfile(profiles as ClientProfile[]);
      }
    } catch {
      setProfile(null);
      setAvailableContexts([]);
    } finally {
      setProfileLoaded(true);
    }
  }, [persistResumableOnboarding, waitForSessionReady]);

  const resolveActiveProfile = useCallback(async (profiles: ClientProfile[]) => {
    const contexts: ProfileContext[] = profiles.map(p => ({
      client_id: p.id,
      client_type: p.client_type,
      label: p.client_type === 'corporate'
        ? (p.company_name || p.full_name || 'Business Account')
        : (p.full_name || 'Personal Account'),
    }));
    setAvailableContexts(contexts);

    // Check if there's a login intent for a specific account type
    const loginIntent = localStorage.getItem(CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY);
    if (loginIntent === 'corporate' || loginIntent === 'individual') {
      const intentProfile = profiles.find(p => p.client_type === loginIntent);
      if (intentProfile) {
        setProfile(intentProfile);
        localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, intentProfile.id);
        localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
        return;
      }
      // No matching profile yet — for corporate logins the auto-accept may still
      // be propagating. Check for open invitations / memberships before giving up.
      if (loginIntent === 'corporate') {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const userEmail = authUser?.email?.toLowerCase();
          if (userEmail) {
            const [{ data: invites }, { data: members }] = await Promise.all([
              supabase
                .from('corporate_invitations')
                .select('id')
                .eq('email', userEmail)
                .in('status', ['pending', 'in_progress', 'accepted'] as any)
                .limit(1),
              supabase
                .from('corporate_members')
                .select('client_id')
                .eq('user_id', authUser!.id)
                .eq('is_active', true)
                .limit(1),
            ]);

            const hasAccess = (invites && invites.length > 0) || (members && members.length > 0);
            if (hasAccess) {
              // Re-fetch member profiles — the auto-accept edge function may have
              // just created the member record moments ago.
              const memberIds = (members || []).map(m => m.client_id);
              if (memberIds.length > 0) {
                const { data: mp } = await supabase
                  .from('client_profiles')
                  .select(PROFILE_SELECT)
                  .in('id', memberIds)
                  .eq('status', 'approved');
                if (mp && mp.length > 0) {
                  const corpProfile = mp[0] as ClientProfile;
                  setProfile(corpProfile);
                  setAvailableContexts([
                    ...contexts,
                    ...mp
                      .filter(p => !contexts.some(c => c.client_id === p.id))
                      .map(p => ({
                        client_id: p.id,
                        client_type: p.client_type,
                        label: p.company_name || p.full_name || 'Business Account',
                      })),
                  ]);
                  localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, corpProfile.id);
                  localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
                  return;
                }
              }
              // Invitation exists but member not yet created — don't flag mismatch,
              // the login page auto-finalization will handle it
              localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
              if (profiles.length > 0) {
                setProfile(profiles[0]);
                localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, profiles[0].id);
              } else {
                setProfile(null);
              }
              return;
            }
          }
        } catch {
          // Best effort — fall through to mismatch
        }
      }

      // Genuinely no matching profile for the selected account type
      localStorage.removeItem(CUSTOMER_ACTIVE_CONTEXT_KEY);
      localStorage.setItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY, loginIntent);
      setProfile(null);
      return;
    }

    // Determine which profile to show
    localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
    const savedContextId = localStorage.getItem(CUSTOMER_ACTIVE_CONTEXT_KEY);
    const savedProfile = savedContextId
      ? profiles.find(p => p.id === savedContextId)
      : null;

    if (savedProfile) {
      setProfile(savedProfile);
    } else if (profiles.length === 1) {
      setProfile(profiles[0]);
      localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, profiles[0].id);
    } else {
      setProfile(profiles[0]);
      localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, profiles[0].id);
    }
  }, []);

  const switchProfile = useCallback(async (clientId: string) => {
    const { data: profileData } = await supabase
      .from('client_profiles')
      .select(PROFILE_SELECT)
      .eq('id', clientId)
      .single();

    if (profileData) {
      setProfile(profileData as ClientProfile);
      localStorage.setItem(CUSTOMER_ACTIVE_CONTEXT_KEY, clientId);
      localStorage.removeItem(CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY);
      localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const activeId = localStorage.getItem(CUSTOMER_ACTIVE_CONTEXT_KEY);
    if (activeId) {
      await switchProfile(activeId);
    }
  }, [switchProfile]);

  useEffect(() => {
    let isMounted = true;

    // Helper: check if user has completed MFA (AAL2)
    // Some users may not have MFA enrolled — treat those as aal2-equivalent
    const checkMfaComplete = async (): Promise<boolean> => {
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        // If user has no MFA factors enrolled, currentLevel stays aal1 but
        // nextLevel is also aal1 — they should still be allowed through.
        if (data?.currentLevel === 'aal2') return true;
        if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal1') return true;
        return false;
      } catch {
        return false;
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted || event === 'INITIAL_SESSION') return;
      // PASSWORD_RECOVERY sessions have no sub claim and must NOT enter the MFA flow.
      // The CustomerResetPassword page handles this event independently.
      if (event === 'PASSWORD_RECOVERY') return;

      if (event === 'SIGNED_OUT') {
        // If sign-in or MFA is in progress, always ignore
        if (signingInRef.current) return;

        // Cancel any previous pending sign-out check
        if (signOutTimerRef.current) {
          clearTimeout(signOutTimerRef.current);
          signOutTimerRef.current = null;
        }

        // Debounce then VERIFY the session is truly gone
        signOutTimerRef.current = setTimeout(async () => {
          if (!isMounted || signingInRef.current) return;
          try {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (!isMounted || signingInRef.current) return;
            if (!currentSession?.user) {
              setSession(null);
              setUser(null);
              setProfile(null);
              setProfileLoaded(true);
            }
          } catch {
            // Network error — assume alive to avoid false logouts
          }
        }, 800);
        return;
      }

      // Any non-SIGNED_OUT event cancels the pending sign-out
      if (signOutTimerRef.current) {
        clearTimeout(signOutTimerRef.current);
        signOutTimerRef.current = null;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      // Sync email change after user confirms via link
      if (event === 'USER_UPDATED' && nextSession?.user?.email) {
        setTimeout(async () => {
          if (!isMounted) return;
          try {
            const BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-api`;
            await fetch(`${BASE}/profile/sync-email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${nextSession.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              },
            });
          } catch { /* best effort */ }
        }, 0);
      }

      if (nextSession?.user) {
        setTimeout(async () => {
          if (!isMounted) return;
          const mfaDone = await checkMfaComplete();
          if (mfaDone) {
            void fetchProfile(nextSession.user.id, true);
          }
        }, 0);
      } else {
        setProfile(null);
        setProfileLoaded(true);
      }
    });

    const bootstrap = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          // Retry MFA check — after MFA verify + redirect,
          // the AAL level can take up to ~3s to propagate.
      let mfaDone = await checkMfaComplete();
          if (!mfaDone) {
            // Retry up to 8x — AAL2 can take ~3s to propagate after MFA verify + redirect
            for (let attempt = 0; attempt < 8; attempt++) {
              if (!isMounted) break;
              await new Promise(r => setTimeout(r, 600));
              mfaDone = await checkMfaComplete();
              if (mfaDone) break;
            }
          }
          if (mfaDone) {
            await fetchProfile(s.user.id);
          } else {
            // User is mid-MFA flow — mark profile as loaded but empty
            setProfile(null);
            setProfileLoaded(true);
          }
        } else {
          setProfile(null);
          setProfileLoaded(true);
        }
        bootstrappedRef.current = true;
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void bootstrap();
    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
    };
  }, [fetchProfile]);

  const setAuthGuard = useCallback((active: boolean) => {
    signingInRef.current = active;
  }, []);

  const loadProfileAfterMfa = useCallback(async () => {
    // Poll until AAL2 is confirmed, then fetch profile
    const checkMfa = async (): Promise<boolean> => {
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        return data?.currentLevel === 'aal2';
      } catch { return false; }
    };
    for (let i = 0; i < 15; i++) {
      const done = await checkMfa();
      if (done) break;
      await new Promise(r => setTimeout(r, 500));
    }
    const { data: { session: s } } = await supabase.auth.getSession();
    if (s?.user) {
      setSession(s);
      setUser(s.user);
      await fetchProfile(s.user.id);
    }
  }, [fetchProfile]);

  const signIn = async (email: string, password: string) => {
    signingInRef.current = true;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    signingInRef.current = false;
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setAvailableContexts([]);
    localStorage.removeItem(CUSTOMER_ACTIVE_CONTEXT_KEY);
    localStorage.removeItem(CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY);
    localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
    // Preserve rememberMe + rememberEmail so the preference survives sign-outs
  };

  const register = async (email: string, password: string): Promise<{ error: string | null }> => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-api/register`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email: email.toLowerCase(), password }),
        }
      );
      const result = await res.json();
      if (result.code === 'NO_ACCOUNT') return { error: result.error || 'No approved application found for this email' };
      if (!res.ok) return { error: result.error || 'Registration failed' };
      return { error: null };
    } catch {
      return { error: 'Network error' };
    }
  };

  return (
    <CustomerAuthContext.Provider value={{ user, session, profile, profileLoaded, profilePending, loading, availableContexts, switchProfile, refreshProfile, signIn, signOut, register, setAuthGuard, loadProfileAfterMfa }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}
